import type { World } from '../World';
import type { InputSnapshot } from '../../input/actions';
import type { CharacterId } from './CharacterDefs';
import { skillParams } from '../abilities/AbilityDefs';
import { SHOT_KINDS, spawnPlayerShot } from '../abilities/shots';
import { skillOutgoingDamageMult } from '../abilities/skillHooks';
import {
  CANDLE_SECONDS, CRAWLER_ENRAGE_SECONDS, FLARE_BUFF_SECONDS, HOUND_DASH_SPEED,
  SUNDER_CHILL_SECONDS, SUNDER_NOVA_DAMAGE, SUNDER_NOVA_RADIUS,
  SUNDER_PERIOD, SUNDER_WINDOW, crawlerCap, slam, spawnCrawler,
} from './aspects';
import { isSpecialEnemy } from '../entities/Enemy';
import { dist2 } from '../../core/math';
import { SIM_HZ } from '../../core/GameLoop';

/**
 * Active abilities (MA): EXACTLY one per character, on ONE shared button,
 * zero aiming — the "hands are for movement" pillar amended to "one stick,
 * one button". Actives SPEND the aspect's own resource where one exists
 * (candle, corpse reserve, aura stacks, HP); plain cooldowns are the
 * fallback. All effects are input-triggered ⇒ live-position/gameplayRng
 * class — zero seeded-stream contact (crawler/hound determinism bucket).
 *
 * Cask has no entry here: his active IS the Shortcut fuse, a scene-level
 * hold-channel (RunScene reads activeHeld — descending is scene machinery).
 * Trine's entry replaces her old automatic Sunder cadence: the dial charges
 * (tickAspects), the PRESS splits.
 */

// ---- Tuning (all provisional — MA is a feel milestone) ----
/** Anti-spam gate for resource-cost actives (their cost is the real limit). */
export const ACTIVE_LOCKOUT = 0.6;
export const HEAVE_CD = 8;
export const HEAVE_DAMAGE = 40;
export const HEAVE_SPLASH = 110;
export const FLARE_CANDLE_SECONDS = 10;
export const EXHUME_MAX = 4;
export const FLICKER_CD = 5;
export const FLICKER_DIST = 140;
export const FLICKER_IFRAME_SECONDS = 0.4;
export const BELLRING_RADIUS = 140;
export const BELLRING_BASE = 15;
export const BELLRING_PER_STACK = 12;
export const FETCH_CD = 6;
export const FETCH_RANGE = 520;
export const BLOODPRICE_CD = 6;

/** UI strings (CharacterScene panel line, PauseOverlay). */
export const ACTIVE_DEFS: Record<CharacterId, { name: string; blurb: string }> = {
  hale: { name: 'Heave', blurb: 'hurl a heavy stone — big hit, wide splash' },
  wick: { name: 'Flare', blurb: 'burn 10s of candle — five seconds of fury' },
  mourne: { name: 'Exhume', blurb: 'spend up to 4 banked corpses — crawlers, now' },
  vane: { name: 'Flicker', blurb: 'dash through — untouchable a breath, knives on arrival' },
  barrow: { name: 'Bellring', blurb: 'discharge every aura stack — slam and shove' },
  trine: { name: 'Sunder', blurb: 'split in a burst of frost; re-form in fire — untouchable between' },
  bramGaunt: { name: 'Fetch', blurb: 'send Gaunt through them, now' },
  cask: { name: 'Fuse', blurb: 'hold — light the fuse and blast a way down, anywhere' },
  thirl: { name: 'Bloodprice', blurb: 'pay 1 HP — the next chain drinks deep' },
};

export interface ActiveStatus {
  /** The press would do something right now. */
  ready: boolean;
  /** Charge fill 0..1 (cooldown progress; 1 for resource-gated actives). */
  frac: number;
  /** Trine only: currently inside the Sunder window (pip draws draining). */
  window: boolean;
}

function cdStatus(world: World, cd: number): ActiveStatus {
  return {
    ready: world.activeCd <= 0,
    frac: 1 - Math.max(0, world.activeCd) / cd,
    window: false,
  };
}

/** HUD pip data (RunScene aspect widget row). */
export function activeStatus(world: World): ActiveStatus {
  const rs = world.runState;
  switch (rs.character) {
    case 'hale': return cdStatus(world, HEAVE_CD);
    case 'wick':
      return rs.flareLeft > 0
        ? { ready: false, frac: rs.flareLeft / FLARE_BUFF_SECONDS, window: true }
        : { ready: world.activeCd <= 0 && rs.candle > 0, frac: 1, window: false };
    case 'mourne': return { ready: world.activeCd <= 0 && rs.corpseReserve > 0, frac: 1, window: false };
    case 'vane': return cdStatus(world, FLICKER_CD);
    case 'barrow': return { ready: world.activeCd <= 0 && world.ironboundStacks > 0, frac: 1, window: false };
    case 'trine':
      return world.sunderLeft > 0
        ? { ready: false, frac: world.sunderLeft / SUNDER_WINDOW, window: true }
        : { ready: world.sunderCd <= 0, frac: 1 - Math.max(0, world.sunderCd) / SUNDER_PERIOD, window: false };
    case 'bramGaunt': {
      const s = cdStatus(world, FETCH_CD);
      const h = world.hound;
      return { ...s, ready: s.ready && h !== null && h.alive };
    }
    case 'cask': return { ready: !world.vault, frac: 1, window: false };
    case 'thirl': {
      const s = cdStatus(world, BLOODPRICE_CD);
      return { ...s, ready: s.ready && world.player.hp > 1 };
    }
  }
}

function hookDamageMult(world: World): number {
  return world.runState.stats.resolve('damageMult') * skillOutgoingDamageMult(world.runState);
}

/**
 * Priority target for actives (Sarah, 2026-07-28): a SPECIAL in range —
 * elite/boss/keeper/goblin — always outranks trash; among each class it's
 * nearest-first. Direct loop; never assumes a fresh hash.
 */
function priorityTargetTo(world: World, x: number, y: number, range: number) {
  let special: World['enemies'][number] | null = null;
  let specialD = range * range;
  let plain: World['enemies'][number] | null = null;
  let plainD = range * range;
  for (const e of world.enemies) {
    if (!e.alive) continue;
    const d = dist2(x, y, e.x, e.y);
    if (isSpecialEnemy(e)) {
      if (d < specialD) { specialD = d; special = e; }
    } else if (d < plainD) {
      plainD = d; plain = e;
    }
  }
  return special ?? plain;
}

/** Fetch's flavor of the same rule: highest-CURRENT-HP special, else biggest. */
function priorityBiggestTo(world: World, x: number, y: number, range: number) {
  let special: World['enemies'][number] | null = null;
  let specialHp = -1;
  let plain: World['enemies'][number] | null = null;
  let plainHp = -1;
  for (const e of world.enemies) {
    if (!e.alive) continue;
    if (dist2(x, y, e.x, e.y) > range * range) continue;
    if (isSpecialEnemy(e)) {
      if (e.hp > specialHp) { specialHp = e.hp; special = e; }
    } else if (e.hp > plainHp) {
      plainHp = e.hp; plain = e;
    }
  }
  return special ?? plain;
}

// slam() (nova + shove) lives in aspects.ts now — the Sunder merge-nova
// fires from tickAspects and needed it without a module cycle.

/** Per-tick active handling (World.update, right after tickAspects). */
export function tickActives(world: World, dt: number, input: InputSnapshot): void {
  if (world.activeCd > 0) world.activeCd -= dt;
  const rs = world.runState;
  const pl = world.player;
  if (!input.activePressed || !pl.alive) return;

  switch (rs.character) {
    case 'hale': { // Heave: one big aimed stone with real splash.
      if (world.activeCd > 0) return;
      const target = priorityTargetTo(world, pl.x, pl.y, 620);
      if (!target) return;
      world.activeCd = HEAVE_CD;
      const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
      spawnPlayerShot(world, pl.x, pl.y, aim, 340, SHOT_KINDS.bolt,
        HEAVE_DAMAGE * hookDamageMult(world), {
          radius: 8, lifeSteps: 150, scale: 1.8,
          splash: HEAVE_SPLASH * rs.stats.resolve('areaMult'),
        });
      break;
    }
    case 'wick': { // Flare (2026-07-28): candle-seconds become FURY — a 5s spike.
      // Mid-window re-press refuses (buff doesn't stack; refusing beats
      // eating a 10s candle spend for nothing).
      if (world.activeCd > 0 || rs.candle <= 0 || rs.flareLeft > 0) return;
      world.activeCd = ACTIVE_LOCKOUT;
      rs.candle = Math.max(0, rs.candle - FLARE_CANDLE_SECONDS / CANDLE_SECONDS);
      rs.flareLeft = FLARE_BUFF_SECONDS;
      world.events.emit('flareIgnited', { x: pl.x, y: pl.y, seconds: FLARE_BUFF_SECONDS });
      break;
    }
    case 'mourne': { // Exhume: the reserve, spent on demand — and the whip.
      const rotBloom = rs.abilities.find((a) => a.defId === 'rotBloom');
      if (world.activeCd > 0 || rs.corpseReserve <= 0 || !rotBloom) return;
      world.activeCd = ACTIVE_LOCKOUT;
      const p = skillParams(rotBloom);
      let n = EXHUME_MAX;
      let spent = 0;
      while (n-- > 0 && rs.corpseReserve > 0 && world.crawlers.length < crawlerCap(world.floor)) {
        rs.corpseReserve--;
        spent++;
        spawnCrawler(world, pl.x, pl.y, p.damage, p.duration);
      }
      // Cap-full press: still spend one corpse as the enrage price — the
      // press must never be a silent whiff, and free enrage would be
      // spammable through the 0.6s lockout.
      if (spent === 0) rs.corpseReserve--;
      world.crawlerEnrage = CRAWLER_ENRAGE_SECONDS;
      world.events.emit('crawlersEnraged', { seconds: CRAWLER_ENRAGE_SECONDS });
      break;
    }
    case 'vane': { // Flicker: dash along the stick (stationary: at the nearest foe).
      if (world.activeCd > 0) return;
      let dx = input.moveX;
      let dy = input.moveY;
      if (dx === 0 && dy === 0) {
        const target = priorityTargetTo(world, pl.x, pl.y, 620);
        if (!target) return;
        dx = target.x - pl.x;
        dy = target.y - pl.y;
      }
      const mag = Math.hypot(dx, dy) || 1;
      dx /= mag;
      dy /= mag;
      world.activeCd = FLICKER_CD;
      // Wall-guarded sub-steps: she slides along walls, never clips through.
      const step = FLICKER_DIST / 5;
      for (let i = 0; i < 5; i++) {
        const nx = pl.x + dx * step;
        const ny = pl.y + dy * step;
        if (pl.moveResolver) {
          [pl.x, pl.y] = pl.moveResolver(pl.x, pl.y, nx, ny, pl.radius * 0.7);
        } else {
          pl.x = nx;
          pl.y = ny;
        }
      }
      pl.prevX = pl.x; // no interpolation smear across the blink
      pl.prevY = pl.y;
      pl.iframes = Math.max(pl.iframes, Math.round(FLICKER_IFRAME_SECONDS * SIM_HZ));
      const fan = rs.abilities.find((a) => a.defId === 'fanKnives');
      if (fan) fan.cooldown = 0; // knives on arrival
      world.spawnSparks(pl.x, pl.y, 8, 1.0);
      break;
    }
    case 'barrow': { // Bellring: the pain battery, discharged.
      if (world.activeCd > 0 || world.ironboundStacks <= 0) return;
      world.activeCd = ACTIVE_LOCKOUT;
      const stacks = world.ironboundStacks;
      world.ironboundStacks = 0;
      world.ironboundFade = 0;
      const radius = BELLRING_RADIUS * rs.stats.resolve('areaMult');
      slam(world, pl.x, pl.y, radius,
        (BELLRING_BASE + BELLRING_PER_STACK * stacks) * hookDamageMult(world),
        50, true); // quiet: the dedicated event below scales VFX with stacks
      world.events.emit('bellringDischarged', { x: pl.x, y: pl.y, radius, stacks });
      break;
    }
    case 'trine': { // Sunder is the press now — the dial charges, SHE splits.
      if (world.sunderLeft > 0 || world.sunderCd > 0) return;
      world.sunderCd = SUNDER_PERIOD;
      world.sunderLeft = SUNDER_WINDOW;
      pl.iframes = Math.max(pl.iframes, 3); // untouchable from the first tick
      // Split bookend (round 5): the split releases a CHILL nova — slam's
      // burstFired ring is already the cool color. Chill per the Rime
      // pattern: max-merge, boss/keeper excluded (set-piece pacing).
      const areaMult = rs.stats.resolve('areaMult');
      const novaR = SUNDER_NOVA_RADIUS * areaMult;
      slam(world, pl.x, pl.y, novaR, SUNDER_NOVA_DAMAGE * hookDamageMult(world), 0);
      for (const e of world.enemies) {
        if (!e.alive || e.type === 'boss' || e.type === 'keeper') continue;
        if (dist2(pl.x, pl.y, e.x, e.y) <= (novaR + e.radius) ** 2) {
          e.chillLeft = Math.max(e.chillLeft, SUNDER_CHILL_SECONDS);
        }
      }
      world.events.emit('sunderStarted', {});
      break;
    }
    case 'bramGaunt': { // Fetch: command the dash NOW.
      const hound = world.hound;
      if (world.activeCd > 0 || !hound || !hound.alive || hound.state !== 'follow') return;
      // The COMMANDED dash goes for the biggest SPECIAL in range, else the
      // biggest anything (Sarah, 2026-07-27/28): pressing Fetch during an
      // elite/boss duel must never send Gaunt chasing trash. Idle charges
      // still pick nearest.
      const target = priorityBiggestTo(world, hound.x, hound.y, FETCH_RANGE);
      if (!target) return;
      world.activeCd = FETCH_CD;
      const aim = Math.atan2(target.y - hound.y, target.x - hound.x);
      hound.dashVx = Math.cos(aim) * HOUND_DASH_SPEED;
      hound.dashVy = Math.sin(aim) * HOUND_DASH_SPEED;
      hound.state = 'dash';
      hound.stateLeft = 0.4;
      hound.dashHit.clear();
      const slip = rs.abilities.find((a) => a.defId === 'slipleash');
      hound.charge = slip ? skillParams(slip).cooldown : 2;
      world.events.emit('houndDashed', { x: hound.x, y: hound.y });
      break;
    }
    case 'cask':
      break; // the fuse channel lives in RunScene (activeHeld)
    case 'thirl': { // Bloodprice: 1 HP for a deep drink.
      if (world.activeCd > 0 || pl.hp <= 1) return;
      world.activeCd = BLOODPRICE_CD;
      pl.hp -= 1; // direct — never lethal (hp > 1 gate), no i-frame dodge
      world.bloodprice = true; // consumed by the next chain fire
      const chain = rs.abilities.find((a) => a.defId === 'leechChain');
      if (chain) chain.cooldown = 0;
      world.events.emit('playerHit', { damage: 1, hpAfter: pl.hp });
      break;
    }
  }
}
